import React from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/sections/Reveal";
import { useSiteImages } from "@/lib/useSiteImages";

const credentials = {
  Awards: [
    "Salesforce MVP Hall of Fame (2023)",
    "Salesforce MVP (2018)",
    "DCFemTech Top Female Engineer (2017, 2016)",
    "Smart CEO Brava Award — Top Female CEO (2017)",
    "TechWeek Rising Star (2017)",
  ],
  "Speaking Stages": [
    "CES 2025",
    "CES 2026",
    "Open Source in Finance Forum NY 2025",
    "DevOpsDays NY 2016",
    "Texas Dreamin",
    "Equality Lounge @ CES 2025",
    "Obama White House Girls in STEM Initiative",
  ],
  Education: [
    "M.A. Business Administration, Howard University",
    "B.A. Sociology, Hampton University",
    "Certificates, Computer Graphics & Web Design, University of Maryland",
  ],
  "Notable Clients": [
    "White House",
    "Apple",
    "Salesforce",
    "Howard University",
    "American Bar Association",
    "Department of Energy",
  ],
};

const ventures = [
  {
    name: "Copper & Vine Studio (est. 2003, rebranded)",
    description: "The operating and technology firm.",
  },
  {
    name: "By Grace Retreats (est. 2014)",
    description:
      "A leadership retreat that has hosted 1,000+ leaders across four continents.",
  },
  {
    name: "StepFwd (est. 2020)",
    description: "A mobile app for social advocacy.",
  },
  {
    name: "Artern",
    description: "A mobile app for personalized gifting.",
  },
  {
    name: "PinStripe Consulting (est. age 19)",
    description: "Where the practice began. Database development.",
  },
];

const pressLogos = [
  "CES",
  "OSFF",
  "RevTalks",
  "Why IT Matters",
  "Spotify Podcast",
  "Salesforce ecosystem press",
  "blackcomputeHER",
  "Savoy Network",
];

export default function About() {
  const IMAGES = useSiteImages();
  return (
    <div>
      {/* HERO */}
      <section
        className="rk-section"
        style={{ paddingTop: "calc(var(--rk-section-py) + 60px)" }}
      >
        <div className="rk-container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="rk-eyebrow">About</div>
              </Reveal>
              <Reveal delay={0.05}>
                <h1 className="rk-heading-page mt-5">The record.</h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 rk-body" style={{ color: "var(--rk-muted)" }}>
                  Who she is. What she has built. Where she has been.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.08} y={28}>
                <div
                  style={{
                    position: "relative",
                    aspectRatio: "4/5",
                    overflow: "hidden",
                    borderRadius: 2,
                    border: "1px solid var(--rk-border)",
                  }}
                >
                  <img
                    src={IMAGES.aboutPortrait}
                    alt="Rakia Finley — portrait"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      filter: "saturate(0.96)",
                    }}
                    loading="eager"
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* NARRATIVE BIO */}
      <section
        className="rk-section-tight"
        style={{ backgroundColor: "var(--rk-surface)" }}
      >
        <div className="rk-container-text">
          <Reveal>
            <div className="rk-eyebrow">Bio — First person</div>
          </Reveal>
          <div className="mt-8 space-y-7" style={{ fontSize: "clamp(18px, 1.4vw, 22px)", lineHeight: 1.55 }}>
            <Reveal delay={0.04}>
              <p>
                I founded my first technology company at nineteen. Not because
                it was bold. Because the work was there and I knew how to do
                it.
              </p>
            </Reveal>
            <Reveal delay={0.06}>
              <p>
                In 2006, I built a custom AI system for Starwood Hotels —
                before “AI” was a conference theme, before it was a boardroom
                imperative. It handled virtual booking and room management. It
                worked.
              </p>
            </Reveal>
            <Reveal delay={0.08}>
              <p>
                I have spent the two decades since doing variations of the
                same thing: arriving before the category exists, building the
                infrastructure, and leaving the organization more capable than
                I found it. I have done this for corporations, federal
                agencies, research institutions, and early-stage companies on
                four continents.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p>
                I am the Founder and Managing Partner of Copper & Vine Studio,
                an operating and technology firm I built from a database
                development practice I started at nineteen into one of the
                longer-standing independent technology firms in the country. I
                am North America’s first Black female founder of a private AI
                software and product development firm — a distinction I do not
                lead with, but one I do not disown.
              </p>
            </Reveal>
            <Reveal delay={0.12}>
              <p>
                I have led AI readiness and governance work for the White
                House and the Department of Energy. I was the Founding CTO of
                a London health company that secured a £6.2M Innovate UK
                grant. I am a Salesforce MVP Hall of Fame inductee. I spoke at
                CES in 2025 and 2026. I hold an M.A. in Business
                Administration from Howard University and a B.A. from Hampton
                University.
              </p>
            </Reveal>
            <Reveal delay={0.14}>
              <p>
                I take a limited number of engagements each quarter. The
                person on the engagement is the person with the experience.
                That is the model.
              </p>
            </Reveal>
            <Reveal delay={0.16}>
              <p>
                If you have found your way to this page, you probably already
                know what you need. The question is whether it is something I
                can help with.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CREDENTIALS */}
      <section className="rk-section">
        <div className="rk-container-wide">
          <Reveal>
            <div className="rk-eyebrow">Credentials</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="rk-heading-section mt-5">Awards, stages, education.</h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-10">
            {Object.entries(credentials).map(([title, items], idx) => (
              <Reveal key={title} delay={idx * 0.05}>
                <div>
                  <h3
                    className="pb-3"
                    style={{
                      borderBottom: "1px solid var(--rk-copper)",
                      fontFamily: "var(--font-heading)",
                      fontSize: "clamp(22px, 2vw, 28px)",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {title}
                  </h3>
                  <ul className="mt-5 space-y-3">
                    {items.map((it) => (
                      <li
                        key={it}
                        className="rk-body"
                        style={{ color: "var(--rk-ink)" }}
                      >
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VENTURE HISTORY */}
      <section
        className="rk-section"
        style={{ backgroundColor: "var(--rk-surface)" }}
      >
        <div className="rk-container-wide">
          <Reveal>
            <div className="rk-eyebrow">Ventures</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="rk-heading-section mt-5">Built across two decades.</h2>
          </Reveal>
          <div className="mt-12">
            {ventures.map((v, i) => (
              <Reveal key={v.name} delay={i * 0.04}>
                <div
                  className="py-7 grid grid-cols-1 md:grid-cols-12 gap-4"
                  style={{
                    borderTop: "1px solid var(--rk-border)",
                    borderBottom:
                      i === ventures.length - 1
                        ? "1px solid var(--rk-border)"
                        : "none",
                  }}
                >
                  <div className="md:col-span-6">
                    <h3
                      style={{
                        fontFamily: "var(--font-heading)",
                        fontSize: "clamp(22px, 1.9vw, 26px)",
                        lineHeight: 1.2,
                      }}
                    >
                      {v.name}
                    </h3>
                  </div>
                  <div className="md:col-span-6">
                    <p className="rk-body" style={{ color: "var(--rk-ink)" }}>
                      {v.description}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRESS LOGOS */}
      <section className="rk-section">
        <div className="rk-container-wide">
          <Reveal>
            <div className="rk-eyebrow">Press / Media</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="rk-heading-section mt-5">As seen and heard.</h2>
          </Reveal>
          <div className="mt-10 flex flex-wrap gap-x-10 gap-y-6">
            {pressLogos.map((l, i) => (
              <Reveal key={l} delay={i * 0.03}>
                <span
                  style={{
                    fontFamily: "var(--font-heading)",
                    fontSize: "clamp(22px, 2vw, 30px)",
                    color: "var(--rk-ink)",
                    letterSpacing: "-0.01em",
                  }}
                >
                  {l}
                </span>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-12">
              <Link to="/contact" className="rk-btn-outline">
                For press inquiries, use the contact form
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
